import type { Feature, MultiPolygon, Polygon } from "geojson";
import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import type { SamplePointCollection } from "../../../domain/sample/types";
import { addPointUseCase } from "../../../application/map/useCases/modifyPointUseCase";
import { isPointInPolygon } from "../../../domain/geometry/spatial/pointInPolygon";

let fallbackCounter = 0;

/** Derive next sample ID from existing points. Increments if all numeric, else alphanumeric fallback. */
export function nextSampleId(fc: SamplePointCollection): string {
	const ids = fc.features.map((f) => f.properties.sample_id);
	const nums = ids.map(Number).filter((n) => Number.isInteger(n) && n >= 0);
	if (nums.length > 0 || ids.length === 0) {
		const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
		return String(next);
	}
	return `pt${Date.now()}${fallbackCounter++}`;
}

export function makePointAddTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;

	return {
		id: "point-add",
		label: "+Pt",
		cursor: "crosshair",
		onActivate() {
			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}
		},
		onMapClick(e: MapClickEvent) {
			if (!jobId) return;
			const currentJobId = jobId;

			const coords = [e.lngLat.lng, e.lngLat.lat];

			// Check if point is inside boundary (skip check if no boundary)
			const boundaryFC = deps.getBoundaryFC(jobId);
			if (boundaryFC && boundaryFC.features.length > 0) {
				const inside = boundaryFC.features.some(
					(f) =>
						(f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon") &&
						isPointInPolygon(coords, f as Feature<Polygon | MultiPolygon>),
				);
				if (!inside) {
					deps.showError("Cannot add point outside the boundary");
					return;
				}
			}

			const pointsFC: SamplePointCollection = deps.getPointsFC(jobId) ?? {
				type: "FeatureCollection",
				features: [],
			};

			const sampleId = nextSampleId(pointsFC);

			const result = addPointUseCase(pointsFC, sampleId, coords, (col) => deps.applyPoints(currentJobId, col));

			if (!result.ok) {
				deps.showError(result.error);
				return;
			}

			deps.undoStack.push(result.data.command);
		},
		onDeactivate() {
			jobId = null;
		},
	};
}
